import React, { useState, useEffect, useCallback } from "react";
import { Container, Spinner, Pagination } from "react-bootstrap";
import { RiMoneyDollarCircleFill } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { PieChart, pieChartDefaultProps } from "react-minimal-pie-chart";
import { useWindowSize } from "react-use";
import { getCoinSupply } from "../slyvex-api-client";

const TopWallets = () => {
  const [circCoins, setCircCoins] = useState("-");
  const [addresses, setAddresses] = useState([]);
  const [totalAddresses, setTotalAddresses] = useState(0);
  const [newWallets, setNewWallets] = useState(0);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [holdingData, setHoldingData] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const [chartKey, setChartKey] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const { width } = useWindowSize();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCircCoins = async () => {
      try {
        const coinSupplyResp = await getCoinSupply();
        setCircCoins(Math.round(coinSupplyResp.circulatingSupply / 100000000));
      } catch (error) {
        console.error("Error fetching coin supply:", error);
      }
    };

    const fetchCSV = async () => {
      try {
        const response = await fetch("https://slyvex.network/SVX_addresses.csv");
        const data = await response.text();
        const rows = data.trim().split("\n");

        const parsedAddresses = rows.slice(1, -1).map((row, index) => {
          const [address, balance] = row.split(",");
          return {
            index,
            address,
            balance: parseFloat(balance) / 100000000,
            note: "",
          };
        });

        const newWalletsLine = rows[rows.length - 1];
        const newWalletsValue = parseInt(newWalletsLine.split(",")[1]);

        setAddresses(parsedAddresses);
        setTotalAddresses(parsedAddresses.length);
        setNewWallets(newWalletsValue);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching the CSV file:", error);
      }
    };

    const fetchData = async () => {
      await fetchCircCoins();
      await fetchCSV();
      setDataReady(true);
    };

    fetchData();
  }, []);

  const calculateCharts = useCallback(() => {
    if (!dataReady || totalAddresses === 0 || circCoins === "-") return;

    const addressesAbove25 = addresses.filter((address) => address.balance > 25);
    const balanceRanges = {
      "> 10M SVX": 0,
      "> 1M SVX": 0,
      "> 100K SVX": 0,
      "> 10K SVX": 0,
      "> 1K SVX": 0,
      "> 100 SVX": 0,
      "> 50 SVX": 0,
      "< 50 SVX": 0,
    };

    addressesAbove25.forEach((address) => {
      const balance = parseFloat(address.balance);
      if (balance >= 10000000) balanceRanges["> 10M SVX"] += 1;
      else if (balance >= 1000000) balanceRanges["> 1M SVX"] += 1;
      else if (balance >= 100000) balanceRanges["> 100K SVX"] += 1;
      else if (balance >= 10000) balanceRanges["> 10K SVX"] += 1;
      else if (balance >= 1000) balanceRanges["> 1K SVX"] += 1;
      else if (balance >= 100) balanceRanges["> 100 SVX"] += 1;
      else if (balance >= 50) balanceRanges["> 50 SVX"] += 1;
      else if (balance > 25) balanceRanges["< 50 SVX"] += 1;
    });

    const totalAddressesAbove25 = addressesAbove25.length || 1;

    const newChartData = [
      {
        title: "> 10M SVX",
        value: (balanceRanges["> 10M SVX"] / totalAddressesAbove25) * 100,
        color: "#5E0000",
      },
      {
        title: "> 1M SVX",
        value: (balanceRanges["> 1M SVX"] / totalAddressesAbove25) * 100,
        color: "#800000",
      },
      {
        title: "> 100K SVX",
        value: (balanceRanges["> 100K SVX"] / totalAddressesAbove25) * 100,
        color: "#A52A2A",
      },
      {
        title: "> 10K SVX",
        value: (balanceRanges["> 10K SVX"] / totalAddressesAbove25) * 100,
        color: "#C0392B",
      },
      {
        title: "> 1K SVX",
        value: (balanceRanges["> 1K SVX"] / totalAddressesAbove25) * 100,
        color: "#E74C3C",
      },
      {
        title: "> 100 SVX",
        value: (balanceRanges["> 100 SVX"] / totalAddressesAbove25) * 100,
        color: "#F08080",
      },
      {
        title: "> 50 SVX",
        value: (balanceRanges["> 50 SVX"] / totalAddressesAbove25) * 100,
        color: "#FA8072",
      },
      {
        title: "< 50 SVX",
        value: (balanceRanges["< 50 SVX"] / totalAddressesAbove25) * 100,
        color: "#FFB6C1",
      },
    ];

    setChartData(newChartData);

    const totalWalletsLabel = totalAddresses > 0 ? `Top ${totalAddresses}` : "All wallets";
    const holdingRanges = {
      "DevFunds": 0,
      "Top 5": 0,
      "Top 10": 0,
      "Top 50": 0,
      "Top 100": 0,
      "Top 500": 0,
      [totalWalletsLabel]: 0,
    };

    addresses.forEach((address, index) => {
      const balance = parseFloat(address.balance);
      
      if (index === 0) holdingRanges["DevFunds"] += balance;
      else if (index >= 1 && index <= 5) holdingRanges["Top 5"] += balance;
      else if (index > 5 && index <= 10) holdingRanges["Top 10"] += balance;
      else if (index > 10 && index <= 50) holdingRanges["Top 50"] += balance;
      else if (index > 50 && index <= 100) holdingRanges["Top 100"] += balance;
      else if (index > 100 && index <= 500) holdingRanges["Top 500"] += balance;
      else holdingRanges[totalWalletsLabel] += balance;
    });

    const totalSupply = circCoins;

    const colors = [
      "#5E0000",
      "#800000",
      "#A52A2A",
      "#C0392B",
      "#E74C3C",
      "#FF4136",
      "#FF6B6B",
      "#FF8C8C",
    ];
    
    const newHoldingData = Object.keys(holdingRanges).map((key, index) => {
      let percentage = (holdingRanges[key] / totalSupply) * 100;
      return {
        title: key,
        value: percentage,
        color: colors[index % colors.length],
      };
    });
    
    const totalPercentage = newHoldingData.reduce((sum, item) => sum + item.value, 0);
    const difference = 100 - totalPercentage;
    if (Math.abs(difference) > 0.01) { 
      const maxIndex = newHoldingData.reduce((maxIdx, item, idx, arr) =>
        item.value > arr[maxIdx].value ? idx : maxIdx, 0
      );
      newHoldingData[maxIndex].value += difference;
    }
    
    setHoldingData(newHoldingData);
    

    setChartKey((prevKey) => prevKey + 1);
  }, [dataReady, addresses, totalAddresses, circCoins]);

  useEffect(() => {
    if (dataReady) {
      calculateCharts();
    }
  }, [dataReady, addresses, circCoins, totalAddresses, calculateCharts]);

  const totalPages = Math.ceil(addresses.length / rowsPerPage);
  const maxPageButtons = 10;
  const halfRange = Math.floor(maxPageButtons / 2);
  let startPage = Math.max(currentPage - halfRange, 1);
  let endPage = startPage + maxPageButtons - 1;
  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(endPage - maxPageButtons + 1, 1);
  }

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = addresses.slice(indexOfFirstRow, indexOfLastRow);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const onClickAddr = (e) => {
    const addr = e.target.innerText;
    navigate(`/addresses/${addr}`);
  };

  return (
    <div className="blocks-page">
      <Container className="webpage px-md-5 blocks-page-overview" fluid>
        <div className="block-overview mb-4">
          <div className="d-flex flex-row w-100">
            <h4 className="block-overview-header text-center w-100 mt-4">
              <RiMoneyDollarCircleFill className="rotate" size="1.7rem" />
              Top Addresses
            </h4>
          </div>
          <div className="block-overview-content">
            {loading ? (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              <>
                <table className="styled-table w-100">
                  <thead>
                    <tr>
                       <th style={{ width: "5%" }}>Rank</th>
                       <th style={{ width: "10%" }}>Balance</th>
                       <th style={{ width: "5%" }}>Tag</th>
                       <th style={{ width: "80%" }}>Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((address, index) => (
                        <tr key={address.address || index} id={address.address}>
                          <td>{address.index}</td>
                          <td>{Number(address.balance).toLocaleString()}</td>
                          <td>
                            {address.index === 0 ? "DevFunds" : ""}
                          </td>
                          <td className="hashh" onClick={onClickAddr}>
                            {address.address}
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {[...Array(endPage - startPage + 1)].map((_, i) => {
                      const page = startPage + i;
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>

                <h4 className="block-overview-header text-center w-100 mt-4">
                  SVX distribution
                </h4>
                <div className="d-flex justify-content-center mt-4">
                  <PieChart
                    key={chartKey}
                    data={chartData}
                    label={({ dataEntry }) =>
                      `${dataEntry.value.toFixed(2)}% ${dataEntry.title}`
                    }
                    lineWidth={50}
                    paddingAngle={5}
                    radius={pieChartDefaultProps.radius - 10}
                    labelStyle={{
                      fill: "#fff",
                      fontSize: "3px",
                      fontFamily: "sans-serif",
                    }}
                    labelPosition={105}
                    style={{
                      maxHeight: width < 768 ? "250px" : "350px",
                      width: "100%",
                    }}
                    lengthAngle={360}
                  />
                </div>

                <div className="d-flex justify-content-center mt-4">
                  {totalAddresses > 0 && (
                    <PieChart
                      key={chartKey + 1}
                      data={holdingData}
                      label={({ dataEntry }) =>
                        `${parseFloat(dataEntry.value).toFixed(2)}% ${dataEntry.title}`
                      }
                      lineWidth={50}
                      paddingAngle={5}
                      radius={pieChartDefaultProps.radius - 10}
                      labelStyle={{
                        fill: "#fff",
                        fontSize: "3px",
                        fontFamily: "sans-serif",
                      }}
                      labelPosition={110}
                      style={{
                        maxHeight: width < 768 ? "250px" : "350px",
                        width: "100%",
                      }}
                      lengthAngle={360}
                    />
                  )}
                </div>

                <div className="d-flex flex-column align-items-center mt-4">
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    Total wallets: {totalAddresses}
                    <br />
                    New wallet since last update: +{newWallets}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default TopWallets;
